/**
 * Centralized copy for the native Member List Deal (MLD) screens: the list,
 * the deal detail and the shared status chip. DS returns codes, never wording
 * (ADR-007), so every label for an MLD code lives here. Placeholders use
 * `{token}` and are filled with `formatMessage`, re-exported from the Book
 * Access tab's strings like `MemberSubscriptionsTab.strings.ts` does.
 *
 * An unknown or missing code renders as `unknown`, never as a guessed state.
 */
export { formatMessage } from '../customerInformation/MemberBooksTab.strings';

export const mldStrings = {
  unknown: 'Unknown',
  none: '—',
  /** The Members sidebar entry that opens `/mld`. */
  sidebarLabel: 'Member List Deals',
  previewLabel: 'Preview · staging',
  previewHint:
    'Staging preview of the native Member List Deal. This is not production data.',

  status: {
    TRIAL: 'Trial',
    ACTIVE: 'Active',
    PAYMENT_DUE: 'Payment due',
    ON_HOLD: 'On hold',
    CANCELLED: 'Cancelled',
  },

  /** The "(?)" help next to the "Payment due" status (chip and stepper). */
  paymentDueHelp: {
    label: 'What does Payment due mean?',
    text: 'Payment due means the card payment for this plan failed and Chargebee is retrying it automatically (dunning). The subscription stays active during the retries. It returns to Active when a retry or support collects the payment, or becomes Cancelled if every retry fails.',
  },

  /** Support's GHL "Sale Type" list wording (ADR-003). */
  saleType: {
    NON_CONTRACTED_SALE: 'Non-Contracted Sale',
    CONTRACTED_SALE: 'Contracted Sale',
    BILLED_THROUGH: 'Billed-through',
    RENEWAL_SALE: 'Renewal Sale',
    FAILED_CREDIT_CARD_COLLECTION: 'Failed Credit Card Collection',
    EXPANSION_SALE_SERVICES: 'Expansion Sale (Services)',
    UPGRADE_DOWNGRADE: 'Upgrade/Downgrade',
    REACTIVATION: 'Reactivation',
  },

  /**
   * The one "who" vocabulary for every MLD screen (ADR-007 decision 1): who
   * took a payment, changed a status or scheduled a cancellation. `SYSTEM` (an
   * import or an event of unknown origin) has no wording on purpose: no who is
   * named for it (parity ADR-005), and the screens show "—".
   */
  who: {
    AUTOMATIC: 'Automatic',
    CUSTOMER: 'Customer',
    SUPPORT: 'Support',
    SUPPORT_WITH_EMAIL: 'Support · {email}',
    AUTHORIFY_SYSTEM: 'Authorify (system)',
  },

  subscriptionType: {
    MONTHLY_NO_CONTRACT: 'Monthly – no contract',
    MONTHLY_CONTRACTED: 'Monthly – contracted',
    ANNUAL_CONTRACTED: 'Annual – contracted',
    ONE_TIME: 'One-time',
  },

  family: {
    AFY: 'AFY',
    RM: 'RM',
    OTHER: 'Other',
  },

  ledgerKind: {
    PAYMENT: 'Payment',
    PAYMENT_FAILED: 'Payment failed',
    STATUS_CHANGE: 'Status change',
    PLAN_CHANGE: 'Plan change',
    CANCELLATION_SCHEDULED: 'Cancellation scheduled',
    CANCELLATION_REMOVED: 'Cancellation removed',
    IMPORTED: 'Imported from Chargebee',
  },

  chip: {
    withContext: '{status} · {context}',
    cancels: 'Cancels {date}',
    backFromPaymentDue: 'Back from payment due {date}',
    trialSale: 'Trial sale',
    cancellationScheduled: 'Cancellation scheduled on {date} by {who}',
    /** The same line when nobody can be named (a SYSTEM entry). */
    cancellationScheduledNoWho: 'Cancellation scheduled on {date}',
  },

  /**
   * Joins the parts of the "Last payment" cell's detail line: amount · sale
   * type · who. A part with no value (no sale type, an imported payment's
   * who) is left out rather than shown as a placeholder.
   */
  lastPaymentSeparator: ' · ',

  /**
   * A paid trial payment's sale-type part when it carries a sale type
   * (ADR-002 decision 1): "Non-Contracted Sale · Trial sale", the same two
   * facts the deal detail shows as the sale type plus the "Trial sale" badge.
   */
  trialPaymentSaleType: '{saleType} · {trialSale}',

  /** A row's date and time, "Oct 8 · 1:30 PM" (`formatMldDateTime`). */
  dateTime: '{date} · {time}',

  /** The deal card's "Last payment" fact (ADR-007 decision 10). */
  lastPayment: {
    none: 'No payment yet',
  },

  /**
   * The one muted line under a ledger whose history starts at an import
   * (ADR-007 decision 7). The `IMPORTED` entry is no longer a dated row.
   */
  importFootnote:
    'Imported from Chargebee on {date}. Earlier history is in Chargebee.',

  /** Labels of the dates that matter in the current status. */
  dates: {
    trialEnd: 'Trial ends',
    /** A paid trial's conversion date, in place of "Trial ends" (ADR-007 decision 9). */
    convertsOn: 'Converts on',
    firstFullCharge: 'First full charge',
    activeSince: 'Active since',
    nextPayment: 'Next billing date',
    cancelsOn: 'Cancels on',
    failedOn: 'Failed on',
    amountDue: 'Amount due',
    onHoldSince: 'On hold since',
    resumesOn: 'Resumes on',
    cancelledOn: 'Cancelled on',
  },

  /** Lifecycle stepper: Trial → Active → On hold / Payment due → Cancelled. */
  stepper: {
    stages: {
      trial: 'Trial',
      active: 'Active',
      interrupted: 'On hold / Payment due',
      cancelled: 'Cancelled',
    },
    range: '{status} {start} – {end}',
    singleDay: '{status} {start}',
    since: '{status} since {start}',
    /** What a stage the deal never reached reads (ADR-007 decision 6). */
    placeholders: {
      noTrial: 'No trial',
      notScheduled: 'Not scheduled',
      scheduled: 'Scheduled {date}',
    },
    /** The visually hidden word that says each stage's state. */
    state: {
      current: 'Current stage',
      visited: 'Visited',
      notReached: 'Not reached',
    },
  },

  /** The Member List Deals list page, `/mld` (picked mock: Option A). */
  list: {
    pageTitle: 'Member List Deals',
    tabsLabel: 'Deal status',
    tabs: {
      all: 'All',
      trial: 'Trial',
      active: 'Active',
      payment_due: 'Payment due',
      on_hold: 'On hold',
      cancelled: 'Cancelled',
    },
    filtersLabel: 'Filters',
    chips: {
      cancelsSoon: 'Cancels soon',
      trialSale: 'Trial sale',
      AFY: 'AFY',
      RM: 'RM',
    },
    chipHints: {
      cancelsSoon: 'Deals with a cancellation scheduled in the next 30 days',
      trialSale: 'Deals that began with a trial',
      AFY: 'Authorify deals only',
      RM: 'Referral Marketing deals only',
    },
    searchLabel: 'Search deals',
    searchPlaceholder: 'Search by name, e-mail or subscription id',
    searchTooShort: 'Type at least 2 characters to search.',
    columns: {
      member: 'Member',
      plan: 'Plan',
      status: 'Status',
      since: 'Since',
      lastPayment: 'Last payment',
      next: 'Next billing date / Cancels on',
    },
    /** What each sort direction of a date column means, for screen readers. */
    sortDirections: {
      lastPayment: { desc: 'newest first', asc: 'oldest first' },
      next: { asc: 'soonest first', desc: 'latest first' },
      since: { asc: 'oldest first', desc: 'newest first' },
    },
    noPlan: 'No plan yet',
    empty: 'No deals yet.',
    emptyFiltered: 'No deals match these filters.',
    error: 'Could not load the deals.',
    retry: 'Try again',
    loading: 'Loading deals',
  },

  /** The deal detail page, `/mld/:subscriptionId` (mock Option A). */
  detail: {
    backToList: 'Back to Member List Deals',
    backToMember: 'Back to member',
    unknownMember: 'Unknown member',
    title: '{member} — {plan}',
    notFound:
      'This deal was not found. It may not have been loaded from Chargebee yet.',
    loadError: 'Could not load this deal. Please try again.',
    retry: 'Try again',
    status: {
      heading: 'Status',
    },
    plan: {
      heading: 'Plan',
      plan: 'Plan',
      price: 'Price',
      subscriptionType: 'Subscription type',
      family: 'Family',
    },
    payments: {
      heading: 'Payments',
      empty: 'No payment recorded yet.',
      columns: {
        date: 'Date',
        result: 'Result',
        /** The payment's own plan, or what a one-time charge bought. */
        item: 'Item',
        amount: 'Amount',
        saleType: 'Sale type',
        who: 'Who',
      },
      paid: 'Paid',
      failed: 'Failed',
      /** Badge of the display-only row built from an import's last payment. */
      imported: 'Imported',
      amountDue: '{amount} due',
      /**
       * A sale-typed payment whose sale amount (the gross invoice total) is
       * not what was paid, e.g. an upgrade paid partly with credits.
       */
      paidAndSale: '{paid} paid · {gross} sale',
    },
    links: {
      heading: 'Links',
      member: 'Open member',
      subscription: 'Subscription in Chargebee',
      customer: 'Customer in Chargebee',
      invoice: 'Invoice {invoiceId} in Chargebee',
    },
    otherDeals: {
      heading: "This member's other deals",
      item: '{family} · {plan} · {subscriptionId}',
    },
    history: {
      heading: 'Status history',
      empty: 'No status change recorded yet.',
      statusChange: 'Status changed from {from} to {to}',
      statusSet: 'Status set to {to}',
      planChange: 'Plan changed from {from} to {to}',
      planSet: 'Plan changed to {to}',
      /** A scheduled cancellation that carries its target date. */
      setToCancel: 'Set to cancel on {date}',
      /** Follows the row's `<time>` ("Oct 8 · 1:30 PM"); left out with no who. */
      whoAfterTime: ' · {who}',
      importedOn: 'Imported from Chargebee on {date}',
    },
    warnings: {
      heading: 'Notes on this deal',
      codes: {
        UNKNOWN_CHARGEBEE_STATUS:
          'Chargebee sent a status this page does not know yet.',
        CUSTOMER_NOT_RESOLVED: 'This deal is not linked to a member yet.',
        NOT_TIED_TO_SUBSCRIPTION:
          'Some Chargebee events could not be tied to this subscription.',
        EVENT_FAILED: 'Some Chargebee events could not be read.',
        LEDGER_CAPPED: 'Only the latest 500 history entries are kept.',
      },
    },
  },
} as const;
